import React from "react"
import * as turf from "@turf/turf"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { KM_PER_NM } from "@/config/defaults"
import { draftDisplayName } from "@/features/route/state/RouteSelectors.jsx"
import { nowUtc } from "@/utils/appNow"
import { findOptimisedFor } from "@/utils/routeHelper"

dayjs.extend(utc)

// Strict finite coercion: "" would coerce to 0 and shadow fallbacks.
function toFinite(v) {
  if (typeof v !== "number" && (typeof v !== "string" || v.trim() === ""))
    return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// Leg distance honoring legtype (0/"greatcircle" = GC, 1/"rhumbline" = RL).
export function legDistanceKm(from, to, legtype) {
  const fromPt = turf.point(from)
  const toPt = turf.point(to)
  const isRhumb =
    legtype === 1 ||
    legtype === "1" ||
    (typeof legtype === "string" && legtype.toLowerCase() === "rhumbline")
  return isRhumb
    ? turf.rhumbDistance(fromPt, toPt, { units: "kilometers" })
    : turf.distance(fromPt, toPt, { units: "kilometers" })
}

function summedLegDistanceKm(features) {
  let total = 0
  for (let i = 0; i < features.length - 1; i++) {
    const from = features[i]?.geometry?.coordinates
    const to = features[i + 1]?.geometry?.coordinates
    if (!Array.isArray(from) || !Array.isArray(to)) continue
    const legtype =
      features[i]?.properties?.legtype ?? features[i]?.properties?.legType
    total += legDistanceKm(from, to, legtype)
  }
  return total
}

const featureTime = (f) => (f?.properties?.timestamp ? dayjs.utc(f.properties.timestamp) : null)

// Remaining distance from now; the in-progress leg is interpolated by time.
function remainingKm(features, now) {
  let remKm = 0
  for (let i = 0; i < features.length - 1; i++) {
    const a = features[i]
    const b = features[i + 1]
    const from = a?.geometry?.coordinates
    const to = b?.geometry?.coordinates
    if (!Array.isArray(from) || !Array.isArray(to)) continue
    const legKm = legDistanceKm(
      from,
      to,
      a?.properties?.legtype ?? a?.properties?.legType,
    )
    const aT = featureTime(a)
    const bT = featureTime(b)
    if (bT && now.isAfter(bT)) continue
    if (aT && bT && now.isAfter(aT) && now.isBefore(bT)) {
      const frac = bT.diff(aT) > 0 ? bT.diff(now) / bT.diff(aT) : 1
      remKm += legKm * Math.max(0, Math.min(1, frac))
    } else {
      remKm += legKm
    }
  }
  return remKm
}

// Cumulative leg_data value (0 at departure, total at arrival) interpolated at now.
function cumulativeAt(features, key, now) {
  let prev = null
  for (const f of features) {
    const t = featureTime(f)
    const v = toFinite(f?.properties?.leg_data?.[key])
    if (!t?.isValid() || v == null) continue
    if (!t.isAfter(now)) {
      prev = { t, v }
      continue
    }
    if (!prev) return null
    const span = t.diff(prev.t)
    return span > 0 ? prev.v + ((v - prev.v) * now.diff(prev.t)) / span : v
  }
  return prev?.v ?? null
}

const hasNumber = (w, key) => toFinite(w?.[key]) != null
const hasZones = (w, key) => Array.isArray(w?.[key]) && w[key].length > 0
const isTrue = (w, key) => w?.[key] === true

// Per-waypoint counting rules of astar's route_summary.
const WARNING_COUNTERS = {
  weatherWarnings: (w) =>
    ["warning_segment_wind_speed_kmh", "warning_segment_wave_height_meter", "warning_segment_current_speed_kmh"]
      .filter((k) => hasNumber(w, k)).length,
  rnwWarnings: (w) =>
    ["warning_is_closed_viaarea", "warning_leg_usable", "warning_vertical_clearance", "warning_water_depth"]
      .filter((k) => isTrue(w, k)).length,
  hraWarnings: (w) => Number(hasZones(w, "warning_segment_high_risk_area")),
  jwlaWarnings: (w) => Number(hasZones(w, "warning_segment_jwla")),
  customAreaWarnings: (w) => Number(hasZones(w, "warning_segment_custom_area")),
  ecaWarnings: (w) => Number(hasZones(w, "warning_segment_eca")),
}

// Warnings on legs not yet completed; segment warnings sit on the leg's start waypoint.
function warningsAhead(features, now, totals) {
  const hasBlobs = features.some((f) => f?.properties?.warnings)
  const counts = Object.fromEntries(Object.keys(WARNING_COUNTERS).map((field) => [field, 0]))
  let nextWaypoint = null
  for (let i = features.length - 1; i >= 0; i--) {
    const f = features[i]
    const end = featureTime(nextWaypoint ?? f)
    if (f?.properties?.type !== "weather_point") nextWaypoint = f
    if (end?.isValid() && !end.isAfter(now)) continue
    for (const [field, count] of Object.entries(WARNING_COUNTERS)) {
      counts[field] += count(f?.properties?.warnings)
    }
  }
  return Object.fromEntries(
    Object.keys(WARNING_COUNTERS).map((field) => {
      const total = totals[field]
      if (total == null) return [field, null]
      if (Number(total) === 0) return [field, 0]
      return [field, hasBlobs ? counts[field] : null]
    }),
  )
}

// Fuel and warnings from now on; null where the route lacks per-waypoint data.
function remainingFrom(features, now, base) {
  const fuelSum = cumulativeAt(features, "sum_consumption_tons", now)
  const costSum = cumulativeAt(features, "sum_fuel_cost_usd", now)
  const minus = (total, done) => (total == null || done == null ? null : Math.max(0, total - done))
  return {
    fuelTons: minus(base.fuelTons, fuelSum),
    fuelCostUsd: minus(base.fuelCostUsd, costSum),
    ...warningsAhead(features, now, base),
  }
}

const REMAINING_FIELDS = ["fuelTons", "fuelCostUsd", ...Object.keys(WARNING_COUNTERS)]
const pickRemaining = (base, map = (v) => v) =>
  Object.fromEntries(REMAINING_FIELDS.map((field) => [field, base[field] == null ? null : map(base[field])]))

// Price basis of the persisted fuel cost: astar-shaped `consumption.prices`
// in the stored request (FE-shaped `consumption_prices` for unsaved drafts).
export function bunkerPricesFromRequest(jsonRequest) {
  const prices =
    jsonRequest?.consumption?.prices ?? jsonRequest?.consumption_prices ?? null
  const usdPerTon = toFinite(prices?.usd_per_ton)
  if (usdPerTon == null) return null
  return { usdPerTon, ecaUsdPerTon: toFinite(prices?.eca_usd_per_ton) }
}

// Comparison record for one route (plan route or draft). Persisted summary
// values win; geometry/timestamps fill the gaps for drafts.
export function buildRouteSummary(route, fallbackName, routeType, options = {}) {
  const {
    id = fallbackName,
    isSuggested = false,
    fuelTons = null,
    vesselName = null,
    // Joined tree summary; plan routes have none on the route object.
    summary = route?.summary,
  } = options
  const optimisedFor = findOptimisedFor(route)
  const resolvedFuelTons = toFinite(summary?.total_consumption_tons) ?? fuelTons
  const fuelCostUsd = toFinite(summary?.total_fuel_cost_usd)
  const features = route?.points?.geojson?.features ?? []
  const base = {
    id,
    name: fallbackName,
    vesselName,
    routeType,
    isSuggested,
    fuelTons: resolvedFuelTons,
    fuelCostUsd,
    bunkerPrices: bunkerPricesFromRequest(route?.jsonRequest),
    cii: summary?.cii ?? null,
    optimisedFor,
    weatherWarnings: summary?.weather_warnings_count ?? null,
    rnwWarnings: summary?.rnw_warnings_count ?? null,
    hraWarnings: summary?.hra_warnings_count ?? null,
    jwlaWarnings: summary?.jwla_warnings_count ?? null,
    customAreaWarnings: summary?.custom_area_warnings_count ?? null,
    ecaWarnings: summary?.eca_warnings_count ?? null,
    speedLimits: summary?.info_speed_limits_count ?? null,
  }
  if (features.length === 0) {
    return {
      ...base,
      departureUtc: null,
      etaUtc: null,
      durationDays: null,
      distanceNm: null,
      remainingDistanceNm: null,
      remainingDurationDays: null,
      remaining: pickRemaining({}),
      waypointCount: 0,
    }
  }

  const firstTimestamp = features[0]?.properties?.timestamp
  const lastTimestamp = features[features.length - 1]?.properties?.timestamp
  const departure = firstTimestamp ? dayjs.utc(firstTimestamp) : null
  const eta = summary?.eta_utc
    ? dayjs.utc(summary.eta_utc)
    : lastTimestamp
      ? dayjs.utc(lastTimestamp)
      : null

  const distanceNm =
    summary?.total_distance_km != null
      ? Number(summary.total_distance_km) / KM_PER_NM
      : summedLegDistanceKm(features) / KM_PER_NM

  const durationDays =
    summary?.total_time_hour != null
      ? Number(summary.total_time_hour) / 24
      : departure && eta && eta.isAfter(departure)
        ? eta.diff(departure, "hour", true) / 24
        : null

  let remainingDistanceNm = distanceNm
  let remainingDurationDays = durationDays
  // Without plan times nothing can be cut: "—" instead of whole-route totals.
  let remaining = pickRemaining({})
  if (departure && eta) {
    const now = nowUtc()
    if (!now.isBefore(eta)) {
      remainingDistanceNm = 0
      remainingDurationDays = 0
      remaining = pickRemaining(base, () => 0)
    } else if (now.isAfter(departure)) {
      remainingDurationDays = eta.diff(now, "hour", true) / 24
      remainingDistanceNm = remainingKm(features, now) / KM_PER_NM
      remaining = remainingFrom(features, now, base)
    } else {
      remaining = pickRemaining(base)
    }
  }

  const finiteOrNull = (v) => (Number.isFinite(v) ? v : null)
  return {
    ...base,
    departureUtc: departure?.isValid() ? departure.toISOString() : null,
    etaUtc: eta?.isValid() ? eta.toISOString() : null,
    durationDays: finiteOrNull(durationDays),
    distanceNm: finiteOrNull(distanceNm),
    remainingDistanceNm: finiteOrNull(remainingDistanceNm),
    remainingDurationDays: finiteOrNull(remainingDurationDays),
    remaining,
    waypointCount: features.length,
  }
}

// Comparison data for the route table: one record per selected plan route
// and per visible draft, active route first (it is the delta baseline).
export default function useComparisonSummaries({
  selectedDraftRoutes,
  vesselPlanRoutes,
  activeRouteId,
  routeSummariesById,
  routeTimestampsById,
  routeConsumption,
}) {
  // Drafts shown on the map (selected + visible), one table tab each.
  const draftTabs = React.useMemo(
    () =>
      (selectedDraftRoutes ?? []).filter(
        (draft) => (draft?.points?.geojson?.features?.length ?? 0) > 0,
      ),
    [selectedDraftRoutes],
  )

  const comparisonSummaries = React.useMemo(() => {
    const items = []
    if (vesselPlanRoutes.length === 0 && draftTabs.length === 0) return items

    for (const draft of draftTabs) {
      items.push({
        computedAt: draft?.points?.timestamp ?? null,
        ...buildRouteSummary(draft, draftDisplayName(draft, draftTabs), "suggested", {
          id: `suggested-route-${draft?.points?.routeId ?? "na"}`,
          isSuggested: true,
          vesselName: draft?.points?.vesselName ?? "Pathfinder Vessel",
          // routeConsumption is last-compute state; ambiguous with >1 draft.
          fuelTons:
            draftTabs.length === 1 &&
            typeof routeConsumption === "number" &&
            Number.isFinite(routeConsumption)
              ? routeConsumption
              : null,
        }),
      })
    }

    for (const route of vesselPlanRoutes) {
      const routeId = route?.points?.routeId ?? route?.routeId ?? ""
      const vesselName =
        route?.points?.vesselName ?? route?.vesselName ?? "Vessel"
      const routeName = route?.points?.name ?? route?.name ?? null
      const displayName = routeName || `${vesselName} ${routeId}`.trim()
      const isActive =
        activeRouteId != null && Number(routeId) === Number(activeRouteId)
      const treeSummary =
        routeId !== "" ? routeSummariesById?.[Number(routeId)] : null
      items.push({
        ...buildRouteSummary(route, displayName, "planned", {
          id: routeId !== "" ? `planned-${routeId}` : `planned-${displayName}`,
          vesselName,
          summary: treeSummary ?? route?.summary,
        }),
        isActive,
        computedAt:
          (treeSummary ?? route?.summary)?.calculated_at ??
          (routeId !== "" ? routeTimestampsById?.[Number(routeId)] : null) ??
          route?.points?.timestamp ??
          null,
        state: route?.points?.state ?? "active",
        routeId: routeId === "" ? null : Number(routeId),
        voyageId: route?.points?.voyageId ?? null,
        vesselId: route?.points?.vesselId ?? null,
      })
    }

    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const aActive = a.item.isActive ? 0 : 1
        const bActive = b.item.isActive ? 0 : 1
        if (aActive !== bActive) return aActive - bActive
        return a.index - b.index
      })
      .map(({ item }) => item)
  }, [draftTabs, vesselPlanRoutes, activeRouteId, routeSummariesById, routeTimestampsById, routeConsumption])

  const baselineSummary = React.useMemo(
    () => comparisonSummaries.find((s) => s.isActive) ?? null,
    [comparisonSummaries],
  )

  return { draftTabs, comparisonSummaries, baselineSummary }
}
